import { DestroyRef, Injectable, WritableSignal, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';

import { KeyValueIsUsedModel, KeyValueType } from '@models/common';
import { CipoErrorModel, CipoHttpErrorResponse } from '@models/error/cipo-error.model';
import {
  DisplayType,
  FieldTypeEnum,
  FormattingType,
  FormattingValueType,
  KeyValueFormattingModel,
  KeyValueRestrictionModel,
  RestrictionType,
  RestrictionValueType,
} from '@models/module/fields/enums';
import { FormFieldModel, FormFieldValueType } from '@models/module/fields/main';
import {
  CipoFieldConfig,
  CipoFieldTypes,
  DataSourceInfoModel,
  FormItem,
} from '@shared/components/fields/common/fields.model';
import { DictionaryStore } from '@signal-store/dictionary.store';
import { UserStore } from '@signal-store/user.store';

@Injectable({
  providedIn: 'root',
})
export class FieldsService {
  private dictionaryStore = inject(DictionaryStore);
  private userStore = inject(UserStore);

  getDataSource(params: DataSourceInfoModel): Observable<KeyValueType[]> {
    const contractId = params.contractId ?? this.userStore.contract()?.id;
    return this.dictionaryStore.getFieldDataSource({ ...params, contractId });
  }

  getRelationDataSource(
    { relationId, contractId, entityInstanceId, moduleId, selectedIds }: DataSourceInfoModel,
    fieldId: number,
  ): Observable<KeyValueIsUsedModel<string, string>[]> {
    const data = {
      contractId: contractId ? contractId : this.userStore.contract()?.id,
      fieldId,
      entityInstanceId,
      moduleId,
      relationId,
      selectedIds,
    };
    return this.dictionaryStore.getRelationDataSource(data);
  }

  /**
   * `config` is a `model()`, so writing to it emits on the owning component's output. The caller's
   * `DestroyRef` is what keeps a datasource that resolves after the field is gone from emitting on a
   * destroyed component — a field is torn down mid request whenever a form is rebuilt.
   */
  loadDataSource(
    config: WritableSignal<CipoFieldConfig>,
    value: FormFieldValueType,
    destroyRef: DestroyRef,
    dataSourceInfo?: DataSourceInfoModel,
    onLoaded?: (options: KeyValueType[]) => void,
  ) {
    const source = dataSourceInfo ?? config()?.dataSourceInfo;
    if (!source) {
      return;
    }

    if (source.relationId) {
      const currentValue = value;
      const selectedIds = !currentValue
        ? []
        : Array.isArray(currentValue)
          ? (currentValue as string[])
          : [currentValue as string];
      this.getRelationDataSource({ ...source, selectedIds }, source.fieldId)
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe(options => {
          config.update(fd => ({ ...fd, options }));
          onLoaded?.(options);
        });
    } else {
      this.getDataSource({ ...source, moduleId: config().moduleId })
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe(options => {
          config.update(fd => ({ ...fd, options }));
          onLoaded?.(options);
        });
    }
  }

  /** Only load datasources if the current value is not present in the options */
  checkOptions(
    config: WritableSignal<CipoFieldConfig>,
    value: FormFieldValueType,
    destroyRef: DestroyRef,
    onLoaded?: (options: KeyValueType[]) => void,
  ) {
    const { options, type, multiple } = config();
    const values = multiple ? (value as string[]) : [value as string];
    if (!value || !values.length) {
      return;
    }
    if ([CipoFieldTypes.Listbox, CipoFieldTypes.Select, CipoFieldTypes.RadioGroup].includes(type)) {
      const hasValueWithoutOption = values.some(val => {
        if (val === null || val === undefined) return false;
        return !options?.some(option => option.key === val.toString());
      });
      if (hasValueWithoutOption) {
        this.loadDataSource(config, value, destroyRef, undefined, onLoaded);
      }
    }
  }

  /**
   * @param field Form field model from BE
   * @param isDraft whether the field is in draft mode or is part of a draft document.
   * @returns
   */
  mapFormFieldModel(field: FormFieldModel, isDraft = false): FormItem {
    const {
      aggregateValue,
      id,
      value,
      label,
      displayType,
      formattings,
      restrictions,
      hasMultipleValues,
      annotation,
      dataSourceId,
      dataSources,
      relationId,
      name,
      x,
      y,
      rows,
      cols,
      weatherDateId,
      systemFieldName,
      weatherLocationId,
    } = field;
    const type = this.getFormFieldType(field);

    const fromRestrictions = this.mapRestrictions(restrictions);
    const fromFormattings = this.mapFormattings(formattings);

    const fieldOptions: CipoFieldConfig = {
      aggregateValue,
      id,
      label,
      type,
      multiple: hasMultipleValues && ![DisplayType.MultiLine, DisplayType.RichText].includes(displayType),
      /* TODO: Delete this after implementing form definition in Angular */
      hasMultipleValues,
      multiline: displayType === DisplayType.MultiLine,
      displayType,
      ...(isDraft && { isDraft }),
      ...(weatherDateId && { weatherDateId }),
      ...(weatherLocationId && { weatherLocationId }),
      ...((dataSourceId || relationId) && { dataSourceInfo: { id: dataSourceId, relationId } }),
      /* dataSources always comes as [] on related list fields. This 'makes' cipo-select to display 'No data found' instead of 'Please wait...' */
      ...(dataSources && { options: this.mapDatasources(dataSources) }),
      ...((x || y || cols || rows) && { position: { x, y, cols, rows } }),
      ...fromRestrictions,
      ...fromFormattings,
      systemFieldName,
      /* Dynamic forms are built from tenant configuration, so a field has no hand-written testId.
         Only system fields get one: `systemFieldName` is the stable identifier a tenant cannot
         rename, while labels and custom field names are theirs to change. */
      ...(systemFieldName && { testId: `dynamic-field-${systemFieldName}` }),
      /* Some form responses contain only the selected datasource options. The same happens for related fields
       populated from document actions, so the available option count cannot determine whether all values are selected. */
      preventAllSelectedLabel: !!dataSources || !!relationId,
      name,
    };

    /* User can reset the select by choosing 'none', if the select is not required and not multiple */
    if (fieldOptions.type === CipoFieldTypes.Select && fieldOptions.multiple === false && !fieldOptions.required) {
      /* TODO: Maybe the default value should be 'none' instead of 'all'? Check cipo-select */
      fieldOptions.noneLabel = 'cipoFields.none';
    }

    return {
      id: id.toString(),
      value: annotation ?? this.mapValue(value, fieldOptions),
      fieldOptions,
    };
  }

  mapValue(value: FormFieldValueType, { type, multiple }: CipoFieldConfig) {
    if (type !== CipoFieldTypes.Select || !value) {
      return value;
    }
    /* For Select fields, always convert to string */
    if (multiple) {
      return (value as (number | string)[]).map(v => v.toString());
    }
    return value?.toString();
  }

  private mapDatasources(dataSources: KeyValueType[] | null | undefined) {
    if (!dataSources?.length) {
      return undefined;
    }
    return dataSources.map(({ key, ...rest }) => {
      /* DataSource returns keys as string or number, but it should be always string. This should be fixed in BE */
      return {
        /* For some reasons, sometimes BE sends a datasource without key. Fallback is a workaround until BE fix */
        key: key?.toString() ?? rest.value?.toString(),
        ...rest,
      };
    });
  }

  private getFormFieldType(field: FormFieldModel): CipoFieldTypes {
    const { displayType, type } = field;
    switch (type) {
      case FieldTypeEnum.Address:
        return CipoFieldTypes.Address;
      case FieldTypeEnum.Attachment:
        return CipoFieldTypes.Attachment;
      case FieldTypeEnum.YesNo:
        return CipoFieldTypes.Checkbox;
      case FieldTypeEnum.Date:
        return CipoFieldTypes.DateTime;
      // TODO: Check if we need different approach for editmode of document details
      case FieldTypeEnum.Relation:
      case FieldTypeEnum.Text:
        switch (displayType) {
          case DisplayType.RichText:
            return CipoFieldTypes.RichText;
          case DisplayType.Select:
            return CipoFieldTypes.Select;
          case DisplayType.Radio:
            return CipoFieldTypes.RadioGroup;
          default:
            return CipoFieldTypes.Text;
        }
      case FieldTypeEnum.Number:
        return CipoFieldTypes.Number;
      case FieldTypeEnum.Annotation:
        return CipoFieldTypes.Annotation;
      case FieldTypeEnum.Table:
        return CipoFieldTypes.Table;
      case FieldTypeEnum.Weather:
        return CipoFieldTypes.Weather;
    }
    // to be completed

    return CipoFieldTypes.Text;
  }

  private mapRestrictions(restrictions: KeyValueRestrictionModel[]) {
    if (!restrictions || restrictions.length === 0) {
      return {};
    }
    const restrictionProps: Partial<CipoFieldConfig> = {};
    restrictions.forEach(r => {
      switch (r.key) {
        case RestrictionType.ReadOnly:
          restrictionProps.readonly = true;
          break;
        case RestrictionType.NeedsToBeChecked:
        case RestrictionType.Required:
          restrictionProps.required = true;
          break;
        case RestrictionType.DateTime:
          switch (r.value) {
            case RestrictionValueType.Date:
              restrictionProps.pickerType = 'date';
              break;
            case RestrictionValueType.Time:
              restrictionProps.pickerType = 'time';
              break;
            case RestrictionValueType.DateTime:
              restrictionProps.pickerType = 'dateTime';
              break;
          }
          break;
        case RestrictionType.MimeTypes:
          // TODO: Investigate why do we have these restrictions on most of the fields that are not related to attachments
          restrictionProps.allowedMimeTypes =
            r.mimeTypesDataSource?.filter(m => r.mimeTypes?.includes(m.key)).map(m => m.value) ?? [];
          break;
        case RestrictionType.ShowThousandSeparator:
          restrictionProps.showThousandSeparator = true;
          break;
        case RestrictionType.MaximumLength:
          restrictionProps.maxLength = r.restrictionValue;
      }
    });
    return restrictionProps;
  }

  private mapFormattings(formattings: KeyValueFormattingModel[]) {
    if (!formattings || formattings.length === 0) {
      return {};
    }
    const formattingProps: Partial<CipoFieldConfig> = {};
    formattings.forEach(f => {
      switch (f.key) {
        case FormattingType.Decimals:
          // it is possible that the field does not have the number restriction but has the decimals defined
          formattingProps.decimals = f.formattingValue;
          break;
        case FormattingType.Number:
          switch (f.value) {
            case FormattingValueType.Percentage:
              formattingProps.suffix = '%';
              break;
            case FormattingValueType.Money:
              formattingProps.currency = this.userStore.currency();
              break;
          }
          break;
        case FormattingType.Text:
          if (f.value === FormattingValueType.Multiline) {
            formattingProps.multiline = true;
          }
          break;
      }
    });
    return formattingProps;
  }
}

/**
 * Compresses grid rows by removing empty rows between occupied rows.
 * This function mutates the input fields array to update their y positions.
 * @param fields - Array of FormItem.
 */
export function compressGridRows(immutableFields: FormFieldModel[]) {
  // 1️Sort fields by y (top to bottom)
  const fields = [...immutableFields].sort((a, b) => a.y - b.y);

  // 2 Build a set of all occupied rows
  const occupiedRows = new Set<number>();
  for (const field of fields) {
    for (let row = field.y; row < field.y + field.rows; row++) {
      occupiedRows.add(row);
    }
  }

  // 3 Create a map from old row numbers to compressed row numbers
  const rowMapping = new Map<number, number>();
  let compressedRow = 0;
  const maxRow = Math.max(...fields.map(f => f.y + f.rows - 1));
  for (let row = 0; row <= maxRow; row++) {
    if (occupiedRows.has(row)) {
      rowMapping.set(row, compressedRow);
      compressedRow++;
    }
  }

  // 4 Mutate each field to update its y based on compressed rows. Return a new array.
  return fields.map(field => {
    const newY = rowMapping.get(field.y);
    return newY === undefined ? field : { ...field, y: newY };
  });
}

/** This boolsheet is required because BE sends the error in different ways.
 *
 * For example, in SOV's "Add Item" dialog, we have 'Line' field. This field is required and needs to be unique.
 *
 * Well, if the field is empty, BE sends:  { line: [{property: "Line", code: "FieldRequired", type: 0, message: "This field is required."}] }
 *
 * But if the field contains a value that already exists, BE sends:  { 253: [{code: "AlreadyExists", type: 0, message: "Already exists."}] }
 *
 * This is why we need to always convert it here, to use the same key / id that is used in the form controls.
 */
export function mapFieldErrors(error: CipoHttpErrorResponse, fields: FormItem[]): (CipoErrorModel & { key: string })[] {
  /* If the error messages are not in the expected format, return early */
  if (typeof error?.messages !== 'object' || !error.messages) {
    return;
  }
  /* Save both id and name (converted first letter to lowercase - that is what C# uses) */
  const fieldsDictionary = (fields ?? []).reduce(
    (acc, field) => {
      const id = field.fieldOptions?.id ?? field.id;
      const name = field.fieldOptions?.name;
      if (id === undefined || id === null) {
        return acc;
      }
      const nameKey = name ? name[0].toLowerCase() + name.slice(1) : null;
      if (nameKey) {
        acc[nameKey] = id.toString();
      }
      acc[id] = id.toString();
      return acc;
    },
    {} as { [key: string]: string },
  );
  /* Only keep the errors that correspond to form controls */
  return Object.entries(error.messages).map(([key, message]) => {
    const fieldKey = fieldsDictionary[key] ?? key;
    return { ...message[0], key: fieldKey } as CipoErrorModel & { key: string };
  });
}
